// Feb 13 Updated TextFieldComponent with transformation logic
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormControl } from '@mui/material';
import CustomFieldWrapper from './CustomFieldWrapper';
import debounce from 'lodash/debounce';
import { applyTransformations } from '../../utils/transformationUtils'; 

const TextFieldComponent = ({
  fieldId, // Treated as UFI
  fieldMetadata = {}, // Default to an empty object if not provided
  value: propValue,
  onChange,
 onBlur,         
 onFocus,        
  disabled = false,
  isSubmitting = false,
  isBuilderMode = false, // Determines if the field is in builder mode
  setShowDropZones, // Function to control the visibility of drop zones
  variant,
  error, // Pass error prop from FieldRenderer
  helperText, // Pass helperText from FieldRenderer
  externalHelperText, // Pass externalHelperText from FieldRenderer
}) => {

  const [internalValue, setInternalValue] = useState(propValue);

  // Log initial value when component mounts
//  console.log(`[TextFieldComponent] Initial propValue for fieldId ${fieldId}:`, propValue);

  // Sync internal state when propValue updates
  useEffect(() => {
    if (propValue !== internalValue) {
//      console.log(`[TextFieldComponent] Syncing propValue -> internalValue for fieldId ${fieldId}:`, propValue);
      setInternalValue(propValue);
    }
  }, [propValue, fieldId]);

  // Memoize transformations to avoid unnecessary re-renders
  const transformations = useMemo(() => fieldMetadata.transformations || {}, [fieldMetadata.transformations]);

  // Ensure transformations object is received
//  console.log(`[TextFieldComponent] Transformations for fieldId ${fieldId}:`, transformations);

  // Handle change and apply transformations
  const handleChange = useCallback((event) => {
    let inputValue = event.target.value;

//    console.log(`[TextFieldComponent] handleChange - Raw input: "${inputValue}"`);

    // ✅ Apply transformations using the transformations object
    inputValue = applyTransformations(inputValue, transformations);

//    console.log(`[TextFieldComponent] handleChange - Transformed input: "${inputValue}"`);

    if (fieldMetadata.maxLength && inputValue.length > fieldMetadata.maxLength) {
      inputValue = inputValue.substring(0, fieldMetadata.maxLength);
//      console.log(`[TextFieldComponent] handleChange - Trimmed input due to maxLength (${fieldMetadata.maxLength}): "${inputValue}"`);
    }

    setInternalValue(inputValue); // ✅ Update local state for real-time display

    // ✅ Immediately notify FieldRenderer (so its local state stays in sync)
    onChange({ target: { value: inputValue } });

    if (isBuilderMode) {
      setShowDropZones(false);
    }
  }, [transformations, fieldMetadata.maxLength, isBuilderMode, setShowDropZones, onChange]);

  // Handle blur and sync final transformed value
const handleBlur = useCallback(() => {
  if (isBuilderMode) {
    setShowDropZones(true);
  }

  // console.log(`[TextFieldComponent] handleBlur - Final transformed value for fieldId ${fieldId}:`, internalValue);

  // 1) Ensure transformed value is sent to FieldRenderer
  onChange({ target: { value: internalValue } });

  // 2) VERY IMPORTANT: call parent onBlur so FieldRenderer.handleBlur can commit setAnswer(...)
 if (typeof onBlur === 'function') {
   try { onBlur(); } catch (_) {}
 }
}, [isBuilderMode, setShowDropZones, internalValue, onChange, onBlur]);


  // Handle submission logic with optional encoding
  useEffect(() => {
    if (isSubmitting) {
      const finalValue = transformations.encode ? encodeURIComponent(internalValue) : internalValue;
//      console.log(`[TextFieldComponent] Submission - Final value for fieldId ${fieldId}:`, finalValue);
      onChange({ target: { value: finalValue } }); // ✅ Ensure correct submission
    }
  }, [isSubmitting, internalValue, onChange, fieldId, transformations.encode]);

//console.log("[TextFieldComponent] Passing fieldId to CustomFieldWrapper:", fieldId);

  return (
    <FormControl fullWidth disabled={disabled || isSubmitting}>


<CustomFieldWrapper
  fieldId={fieldId}
  label={fieldMetadata.label || 'Field'}
  variant={variant}
  value={internalValue}
  onChange={handleChange}
  onBlur={handleBlur}          // our local blur (which now calls parent onBlur too)
 onFocus={onFocus}            // forward parent onFocus to the input (so FieldRenderer sets isEditing=true)
  required={fieldMetadata.required}
  error={error}
  helperText={helperText}
  externalHelperText={externalHelperText}
  placeholder={fieldMetadata.hint || 'Enter text'}
  fullWidth
  InputProps={{
    inputProps: {
      maxLength: fieldMetadata.maxLength,
    },
  }}
  disabled={disabled || isSubmitting}
/>

    </FormControl>
  );
};

TextFieldComponent.propTypes = {
  fieldId: PropTypes.string.isRequired,
  fieldMetadata: PropTypes.shape({
    label: PropTypes.string,
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    externalError: PropTypes.bool,
    externalHelperText: PropTypes.string,
    helperText: PropTypes.string,
    hint: PropTypes.string,
    transformations: PropTypes.object,
  }),
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isBuilderMode: PropTypes.bool,
  setShowDropZones: PropTypes.func, // Ensure this prop type is declared if used
  variant: PropTypes.string, // Add variant to PropTypes
  error: PropTypes.bool, // Ensure error prop is passed
  helperText: PropTypes.string, // Ensure helperText prop is passed
  externalHelperText: PropTypes.string, // External helper text for validation errors
};

export default TextFieldComponent;
